// Chart.js plugin that draws a vertical line at the hovered x index
export const hoverLinePlugin = {
    id: 'hoverLine',
    afterDraw(chart) {
        if (!chart.tooltip) return;
        const active = chart.tooltip.getActiveElements();
        if (!active || !active.length) return;
        const ctx = chart.ctx;
        const x = active[0].element.x;
        const { top, bottom } = chart.scales.y;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(8,43,65,0.15)';
        ctx.stroke();
        ctx.restore();
    },
};

// Maps JS filter keys to the data key prefix used in the new JSON structure
export const FILTER_PREFIX_MAP = {
    region: 'Region',
    band: 'Band',
    industry: 'Industry',
    ownershipType: 'Ownership',
};

// Data is biannual (Q2 + Q4 only), so 2 data points per year
export const QUARTER_COUNTS = { '2yr': 4, '5yr': 10, '10yr': 20 };

// Strips BOM from first key and normalises spaces after colons (e.g. "Industry: Construction" → "Industry:Construction")
export function normalizeData(data) {
    return data.map(row => {
        const normalized = {};
        for (const [k, v] of Object.entries(row)) {
            normalized[k.replace(/^\ufeff/, '').replace(/:\s+/g, ':')] = v;
        }
        return normalized;
    });
}

export function hasActiveFilters(activeFilters) {
    return Object.values(activeFilters).some(v => v !== '');
}

// Returns the data key prefix for the active filter, e.g. "Region:South", or null if none active
export function getActiveSegmentKey(activeFilters) {
    for (const [key, value] of Object.entries(activeFilters)) {
        if (value) return `${FILTER_PREFIX_MAP[key]}:${value}`;
    }
    return null;
}

export function fillSelect(select, values, labelFn, placeholder = 'All') {
    if (!select) return;
    const opts = values.map(v => {
        const label = labelFn ? labelFn(v) : v;
        return `<option value="${v}">${label}</option>`;
    }).join('');
    select.innerHTML = `<option value="">${placeholder}</option>${opts}`;
}

const BAND_ORDER = ['Low', 'Core', 'Large'];
const BAND_LABELS = { Low: '$10m – $50m', Core: '$50m – $100m', Large: '$100m – $1b' };

export function buildFilterSelects(panel, rawData) {
    if (!rawData.length) return;

    const keys = Object.keys(rawData[0]);
    const options = { region: new Set(), band: new Set(), industry: new Set(), ownershipType: new Set() };
    const prefixToKey = { Region: 'region', Band: 'band', Industry: 'industry', Ownership: 'ownershipType' };

    for (const key of keys) {
        const parts = key.split(':');
        const prefix = parts[0];
        const value = parts[1];
        if (prefixToKey[prefix] && value) {
            options[prefixToKey[prefix]].add(value);
        }
    }

    const bandSorted = BAND_ORDER.filter(b => options.band.has(b));

    fillSelect(panel.querySelector('[data-filter-key="region"]'),
        [...options.region].sort(), null, 'All Regions');
    fillSelect(panel.querySelector('[data-filter-key="band"]'),
        bandSorted, b => BAND_LABELS[b] ?? b, 'All Revenue Bands');
    fillSelect(panel.querySelector('[data-filter-key="industry"]'),
        [...options.industry].sort(), null, 'All Industries');
    fillSelect(panel.querySelector('[data-filter-key="ownershipType"]'),
        [...options.ownershipType].sort(), null, 'All Ownership Types');
}

export function attachTimeListeners(buttons, onChange) {
    buttons.forEach(btn => {
        btn.addEventListener('click', () => {
            buttons.forEach(b => b.setAttribute('aria-pressed', 'false'));
            btn.setAttribute('aria-pressed', 'true');
            onChange(btn.dataset.timeFilter);
        });
    });
}

// Only one filter may be active at a time. Selecting any filter clears the rest.
export function attachFilterListeners(panel, activeFilters, onChange) {
    const allSelects = [...panel.querySelectorAll('select[data-filter-key]')];
    const clearBtn = panel.querySelector('.chart-filter-clear');

    function syncClearBtn() {
        if (!clearBtn) return;
        const activeSel = allSelects.find(sel => sel.value !== '');
        clearBtn.hidden = !activeSel;
        if (activeSel) {
            const fieldLabel = document.querySelector(`label[for="${activeSel.id}"]`);
            const prefix = (activeSel.dataset.filterLabel || (fieldLabel ? fieldLabel.textContent.trim() : '')) + ': ';
            clearBtn.textContent = prefix + activeSel.options[activeSel.selectedIndex].text;
        }
    }

    allSelects.forEach(sel => {
        sel.addEventListener('change', () => {
            for (const key of Object.keys(activeFilters)) {
                activeFilters[key] = '';
            }
            activeFilters[sel.dataset.filterKey] = sel.value;
            allSelects.forEach(other => {
                if (other !== sel) other.value = '';
            });
            syncClearBtn();
            onChange();
        });
    });

    if (clearBtn) {
        clearBtn.addEventListener('click', () => {
            for (const key of Object.keys(activeFilters)) {
                activeFilters[key] = '';
            }
            allSelects.forEach(sel => { sel.value = ''; });
            syncClearBtn();
            onChange();
        });
    }
}

export function getVisibleCombos(rawData, activeTime) {
    const allCombos = [...new Set(rawData.map(d => `${d.Year}|${d.Quarter}`))]
        .sort((a, b) => {
            const [ay, aq] = a.split('|').map(Number);
            const [by, bq] = b.split('|').map(Number);
            return ay !== by ? ay - by : aq - bq;
        });

    const visibleCombos = (activeTime in QUARTER_COUNTS)
        ? allCombos.slice(-QUARTER_COUNTS[activeTime])
        : allCombos;

    return { visibleCombos, comboSet: new Set(visibleCombos) };
}
